import {
	CheckCircle,
	ChevronRight,
	ExternalLink,
	HelpCircle,
	Info,
	Plus,
	Table,
	TestTube,
	Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNodeValidation } from '../../hooks/useNodeValidation';
import type { GoogleSheetsNodeConfig } from '../../types/nodes';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';
import { VariablePicker } from '../VariablePicker';

interface GoogleSheetsConfigProps {
	nodeId: string;
	data: any;
	onChange: (data: any) => void;
	onWizardStateChange?: React.Dispatch<
		React.SetStateAction<{
			wizardMode: boolean;
			currentStep: number;
			totalSteps: number;
			canProceedToNext: boolean;
			onPreviousStep: () => void;
			onNextStep: () => void;
			onCompleteWizard: () => Promise<void>;
			validationAttempted: boolean;
			validationFailed: boolean;
			isCompletingSetup: boolean;
		}>
	>;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

const defaultConfig: GoogleSheetsNodeConfig = {
	spreadsheetId: '',
	range: 'Sheet1!A2',
	values: [['{{submission.name}}', '{{submission.email}}']],
	append: true,
};

const WIZARD_STEPS = [
	{ id: 1, title: 'Connect Spreadsheet', icon: Table },
	{ id: 2, title: 'Choose Write Target', icon: ChevronRight },
	{ id: 3, title: 'Configure Data', icon: Plus },
	{ id: 4, title: 'Review & Test', icon: CheckCircle },
	{ id: 5, title: 'Test Write', icon: TestTube },
];

export function GoogleSheetsConfig({
	nodeId,
	data,
	onChange,
	onWizardStateChange,
	onValidationRequest,
}: GoogleSheetsConfigProps) {
	const { validateNode, isValidating, lastValidationResult, clearValidationResult } =
		useNodeValidation();

	// Validation state for two-step complete setup
	const [validationAttempted, setValidationAttempted] = useState(false); // Track if validation has been attempted
	const [validationFailed, setValidationFailed] = useState(false); // Track if validation failed on previous attempt

	const [config, setConfig] = useState<GoogleSheetsNodeConfig>(() => {
		const existingConfig = data.config as Partial<GoogleSheetsNodeConfig>;
		return {
			...defaultConfig,
			...existingConfig,
		};
	});

	const [currentStep, setCurrentStep] = useState(1);
	const [wizardMode, setWizardMode] = useState(true);

	// Update parent data when config changes
	useEffect(() => {
		onChange({
			...data,
			config,
		});
	}, [config, data, onChange]);

	// Step validation
	const canProceedToStep = (step: number) => {
		switch (step) {
			case 1:
				return true; // Always can start
			case 2:
				return config.spreadsheetId.trim() !== '';
			case 3:
				return config.spreadsheetId.trim() !== '' && config.range.trim() !== '';
			case 4:
				return (
					config.spreadsheetId.trim() !== '' &&
					config.range.trim() !== '' &&
					config.values.length > 0 &&
					config.values[0].length > 0
				);
			default:
				return false;
		}
	};

	const isStepValid = (step: number) => {
		return canProceedToStep(step + 1);
	};

	const goToStep = (step: number) => {
		if (canProceedToStep(step)) {
			setCurrentStep(step);
		}
	};

	const handleConfigChange = (updates: Partial<GoogleSheetsNodeConfig>) => {
		setConfig(prev => ({ ...prev, ...updates }));
	};

	// Validation function that can be called by parent components
	const performValidation = useCallback(async (): Promise<boolean> => {
		try {
			const result = await validateNode(nodeId, data);

			// Return true if validation passed, false if there are critical errors
			return result.valid || !result.errors.some(error => error.severity === 'error');
		} catch (error) {
			console.error('Validation failed:', error);
			return false;
		}
	}, [validateNode, nodeId, data]);

	// Register validation function with parent
	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(performValidation);
		}
	}, [onValidationRequest, performValidation]);

	// Wizard state management
	useEffect(() => {
		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode,
				currentStep,
				totalSteps: 4,
				canProceedToNext: canProceedToStep(currentStep + 1),
				onPreviousStep: () => setCurrentStep(Math.max(1, currentStep - 1)),
				onNextStep: () => setCurrentStep(Math.min(4, currentStep + 1)),
				onCompleteWizard: async () => {
					// Step 1: If validation hasn't been attempted yet, run validation and stay in node
					if (!validationAttempted) {
						try {
							const result = await validateNode(nodeId, data);
							setValidationAttempted(true);

							if (!result.valid) {
								// Validation failed - stay in node and show errors
								setValidationFailed(true);
								console.log('Validation failed, staying in node:', result);
								return; // Don't exit the node
							} else {
								// Validation succeeded - proceed to exit
								setValidationFailed(false);
								console.log('Validation succeeded:', result);
							}
						} catch (error) {
							console.error('Validation failed:', error);
							setValidationAttempted(true);
							setValidationFailed(true);
							return; // Don't exit the node on error
						}
					}

					// Step 2: If validation was already attempted (regardless of success/failure), exit the node
					// This allows users to force exit even if validation failed

					setWizardMode(false);
					setTimeout(() => setCurrentStep(1), 100);

					// Reset validation state for next time
					setValidationAttempted(false);
					setValidationFailed(false);
				},
				validationAttempted,
				validationFailed,
			});
		}
	}, [
		wizardMode,
		currentStep,
		config,
		onWizardStateChange,
		validationAttempted,
		validationFailed,
		validateNode,
		nodeId,
		data,
	]);

	// Step 1: Connect Spreadsheet
	const renderStep1 = () => (
		<div className="space-y-6">
			<div className="text-center">
				<Table className="w-12 h-12 mx-auto mb-4 text-purple-600" />
				<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
					Connect to Google Sheets
				</h3>
				<p className="text-gray-600 dark:text-gray-400">
					Enter your Google Sheets spreadsheet ID to connect to your spreadsheet.
				</p>
			</div>

			<div className="space-y-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Spreadsheet ID
					</label>
					<VariablePicker
						nodeId={nodeId}
						value={config.spreadsheetId}
						onChange={value => handleConfigChange({ spreadsheetId: value })}
						placeholder="e.g., 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
						className="w-full"
					/>
					<div className="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
						<HelpCircle className="w-4 h-4 mr-1" />
						<span>Where to find this?</span>
						<button
							type="button"
							onClick={() =>
								window.open(
									'https://docs.google.com/spreadsheets/d/YOUR_SPREADSHEET_ID/edit',
									'_blank'
								)
							}
							className="ml-1 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 flex items-center"
						>
							<ExternalLink className="w-3 h-3 mr-1" />
							Example
						</button>
					</div>
				</div>

				<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
					<div className="flex items-start">
						<Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
						<div className="text-sm text-blue-800 dark:text-blue-200">
							<p className="font-medium mb-1">How to find your Spreadsheet ID:</p>
							<ol className="list-decimal list-inside space-y-1 text-xs">
								<li>Open your Google Sheets document</li>
								<li>Look at the URL in your browser</li>
								<li>
									Copy the long string between{' '}
									<code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">
										/d/
									</code>{' '}
									and{' '}
									<code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">
										/edit
									</code>
								</li>
								<li>That's your Spreadsheet ID</li>
							</ol>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	// Step 2: Choose Write Target
	const renderStep2 = () => (
		<div className="space-y-6">
			<div className="text-center">
				<ChevronRight className="w-12 h-12 mx-auto mb-4 text-purple-600" />
				<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
					Choose Write Target
				</h3>
				<p className="text-gray-600 dark:text-gray-400">
					Specify where in your spreadsheet you want to write data.
				</p>
			</div>

			<div className="space-y-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Range
					</label>
					<VariablePicker
						nodeId={nodeId}
						value={config.range}
						onChange={value => handleConfigChange({ range: value })}
						placeholder="e.g., Sheet1!A2, Data!B5, A1"
						className="w-full"
					/>
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						Format: SheetName!CellReference (e.g., Sheet1!A2)
					</p>
				</div>

				<div>
					<label className="flex items-center space-x-2">
						<input
							type="checkbox"
							checked={config.append}
							onChange={e => handleConfigChange({ append: e.target.checked })}
							className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
						/>
						<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
							Append to sheet
						</span>
					</label>
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						{config.append
							? 'Data will be added to the next available row'
							: 'Data will overwrite existing content in the specified range'}
					</p>
				</div>
			</div>
		</div>
	);

	// Step 3: Configure Data
	const renderStep3 = () => (
		<div className="space-y-6">
			<div className="text-center">
				<Plus className="w-12 h-12 mx-auto mb-4 text-purple-600" />
				<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
					Configure Data to Write
				</h3>
				<p className="text-gray-600 dark:text-gray-400">
					Define the data you want to write to your spreadsheet. Each row represents a row
					in the sheet.
				</p>
			</div>

			<div className="space-y-4">
				<div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
					<div className="flex items-start">
						<Info className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
						<div className="text-sm text-yellow-800 dark:text-yellow-200">
							<p className="font-medium mb-1">Token Support:</p>
							<p>
								You can use tokens like{' '}
								<code className="bg-yellow-100 dark:bg-yellow-800 px-1 rounded">
									{'{{submission.email}}'}
								</code>
								,{' '}
								<code className="bg-yellow-100 dark:bg-yellow-800 px-1 rounded">
									{'{{nodes.api.output.data.id}}'}
								</code>
								, etc.
							</p>
						</div>
					</div>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Data Values (2D Array)
					</label>
					<div className="space-y-2">
						{config.values.map((row, rowIndex) => (
							<div key={rowIndex} className="flex items-center space-x-2">
								{row.map((cell, cellIndex) => (
									<div key={cellIndex} className="flex-1">
										<VariablePicker
											nodeId={nodeId}
											value={cell}
											onChange={value => {
												const newValues = [...config.values];
												newValues[rowIndex][cellIndex] = value;
												handleConfigChange({ values: newValues });
											}}
											placeholder={`Cell ${rowIndex + 1}-${cellIndex + 1}`}
											className="w-full text-sm"
										/>
									</div>
								))}
								<button
									type="button"
									onClick={() => {
										const newValues = [...config.values];
										newValues[rowIndex].push('');
										handleConfigChange({ values: newValues });
									}}
									className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
								>
									<Plus className="w-4 h-4" />
								</button>
								<button
									type="button"
									onClick={() => {
										const newValues = [...config.values];
										if (newValues[rowIndex].length > 1) {
											newValues[rowIndex].pop();
											handleConfigChange({ values: newValues });
										}
									}}
									className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
								>
									<Trash2 className="w-4 h-4" />
								</button>
							</div>
						))}
					</div>

					<div className="flex space-x-2 mt-3">
						<button
							type="button"
							onClick={() => {
								const newValues = [...config.values];
								const newRow = new Array(config.values[0]?.length || 1).fill('');
								newValues.push(newRow);
								handleConfigChange({ values: newValues });
							}}
							className="flex items-center px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-300 dark:hover:bg-purple-800"
						>
							<Plus className="w-3 h-3 mr-1" />
							Add Row
						</button>
						<button
							type="button"
							onClick={() => {
								const newValues = [...config.values];
								if (newValues.length > 1) {
									newValues.pop();
									handleConfigChange({ values: newValues });
								}
							}}
							className="flex items-center px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 dark:bg-red-900 dark:text-red-300 dark:hover:bg-red-800"
						>
							<Trash2 className="w-3 h-3 mr-1" />
							Remove Row
						</button>
					</div>
				</div>
			</div>
		</div>
	);

	// Step 4: Review & Save
	const renderStep4 = () => (
		<div className="space-y-6">
			<div className="text-center">
				<CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-600" />
				<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
					Review Configuration
				</h3>
				<p className="text-gray-600 dark:text-gray-400">
					Review your Google Sheets configuration before saving.
				</p>
			</div>

			<div className="space-y-4">
				<div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
					<div>
						<label className="text-sm font-medium text-gray-700 dark:text-gray-300">
							Spreadsheet ID:
						</label>
						<p className="text-sm text-gray-900 dark:text-white font-mono bg-white dark:bg-gray-700 px-2 py-1 rounded border">
							{config.spreadsheetId || 'Not set'}
						</p>
					</div>

					<div>
						<label className="text-sm font-medium text-gray-700 dark:text-gray-300">
							Range:
						</label>
						<p className="text-sm text-gray-900 dark:text-white font-mono bg-white dark:bg-gray-700 px-2 py-1 rounded border">
							{config.range || 'Not set'}
						</p>
					</div>

					<div>
						<label className="text-sm font-medium text-gray-700 dark:text-gray-300">
							Write Mode:
						</label>
						<p className="text-sm text-gray-900 dark:text-white">
							{config.append ? 'Append' : 'Overwrite'}
						</p>
					</div>

					<div>
						<label className="text-sm font-medium text-gray-700 dark:text-gray-300">
							Data Structure:
						</label>
						<div className="text-sm text-gray-900 dark:text-white">
							{config.values.length} row{config.values.length !== 1 ? 's' : ''} ×{' '}
							{config.values[0]?.length || 0} column
							{config.values[0]?.length !== 1 ? 's' : ''}
						</div>
					</div>
				</div>

				<div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
					<div className="flex items-start">
						<CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 mr-3 flex-shrink-0" />
						<div className="text-sm text-green-800 dark:text-green-200">
							<p className="font-medium">Configuration Complete!</p>
							<p>
								Your Google Sheets node is ready to write data to your spreadsheet.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	if (!wizardMode) {
		// Show a simplified view when not in wizard mode
		return (
			<div className="p-6 space-y-4">
				{/* Validation Results */}
				{(isValidating || lastValidationResult) && (
					<ValidationResultDisplay
						errors={lastValidationResult?.errors || []}
						isValidating={isValidating}
						onDismiss={clearValidationResult}
					/>
				)}

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Spreadsheet ID
					</label>
					<VariablePicker
						nodeId={nodeId}
						value={config.spreadsheetId}
						onChange={value => handleConfigChange({ spreadsheetId: value })}
						placeholder="Enter spreadsheet ID"
						className="w-full"
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Range
					</label>
					<VariablePicker
						nodeId={nodeId}
						value={config.range}
						onChange={value => handleConfigChange({ range: value })}
						placeholder="e.g., Sheet1!A2"
						className="w-full"
					/>
				</div>

				<div>
					<label className="flex items-center space-x-2">
						<input
							type="checkbox"
							checked={config.append}
							onChange={e => handleConfigChange({ append: e.target.checked })}
							className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
						/>
						<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
							Append to sheet
						</span>
					</label>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col h-full">
			{/* Wizard Steps Header */}
			<div className="flex bg-gray-50 border-b border-gray-200 transition-colors dark:bg-gray-800 dark:border-gray-700">
				{WIZARD_STEPS.map(step => {
					const isActive = currentStep === step.id;
					const isCompleted = isStepValid(step.id);
					const canAccess = canProceedToStep(step.id);
					const Icon = step.icon;

					return (
						<button
							key={step.id}
							disabled={!canAccess}
							className={`flex-1 px-3 py-3 text-xs font-medium transition-colors ${
								isActive
									? 'border-b-2 border-purple-500 dark:border-purple-400 text-purple-600 dark:text-purple-400 bg-white dark:bg-gray-700'
									: canAccess
									? 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
									: 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
							}`}
							onClick={() => canAccess && goToStep(step.id)}
						>
							<div className="flex flex-col items-center gap-1">
								<div className="flex items-center gap-1">
									<Icon className="w-4 h-4" />
									{isCompleted && (
										<CheckCircle className="w-3 h-3 text-green-500" />
									)}
								</div>
								<span className="text-center leading-tight">{step.title}</span>
							</div>
						</button>
					);
				})}
			</div>

			{/* Step Content */}
			<div className="overflow-auto flex-1 p-6 bg-white transition-colors dark:bg-gray-900">
				{/* Validation Results */}
				{(isValidating || lastValidationResult) && (
					<ValidationResultDisplay
						errors={lastValidationResult?.errors || []}
						isValidating={isValidating}
						onDismiss={clearValidationResult}
					/>
				)}

				{currentStep === 1 && renderStep1()}
				{currentStep === 2 && renderStep2()}
				{currentStep === 3 && renderStep3()}
				{currentStep === 4 && renderStep4()}
			</div>
		</div>
	);
}
